<template>
  <BaseScreen v-bind="screen">
    <template #actions>
      <BaseButton
        v-for="(item, i) in actions"
        :key="`action_${i}`"
        v-bind="item"
        @click="item.action"
      />
    </template>
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Camera>
      <BaseOverlay
        v-if="isVideoReady"
        v-bind="watchOverlay"
        slot="overlay"
      />
    </Camera>
    <ModalConfirm
      v-if="isAbortModalVisible"
      v-bind="abortModal"
      slot="root"
      class="screen__overlay"
      @agree="abortRecording"
      @disagree="hideAbortModal"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import screenMixin from '@/mixins/screen';
import cameraMixin from '@/mixins/camera';
import timerMixin from '@/mixins/timer';

import Camera from '@/components/Camera.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseOverlay from '@/components/BaseOverlay.vue';
import ModalConfirm from '@/components/ModalConfirm.vue';

const MAX_VIDEO_DURATION_IN_SECONDS = 30;

export default {
  name: 'VideoRecordingScreen',
  mixins: [screenMixin, cameraMixin, timerMixin],
  components: {
    Camera,
    BaseButton,
    BaseOverlay,
    ModalConfirm,
  },
  data() {
    return {
      isRecording: false,
      isAbortModalVisible: false,
      isVideoProcessing: false,
      isCameraStarted: false,
      shouldAbort: false,
      time: '00:00',
    };
  },
  computed: {
    ...mapState('feedback', [
      'video',
    ]),
    titleText() {
      if (this.isRecording) {
        const max = this.getTimerPrettyValue(MAX_VIDEO_DURATION_IN_SECONDS * 1000);
        return `<span class="stopwatch stopwatch_has_indicator">${this.time}</span> / ${max}`;
      }
      return this.isVideoReady ? this.$t('feedback.videoReady') : '';
    },
    pendingText() {
      return this.isVideoProcessing
        ? this.$t('feedback.processing')
        : this.$t('feedback.hardwarePending');
    },
    screenCase() {
      if (this.isErrorVisible) {
        return 'STEP_HARDWARE_ERROR';
      }
      if (this.isVideoReady) {
        return 'STEP_VIDEO_READY';
      }
      return null;
    },
    actions() {
      if (this.isVideoReady || this.isVideoProcessing) {
        return [];
      }
      if (this.isRecording) {
        return [
          // {
          //   text: this.$t('feedback.abortRecord'),
          //   action: this.showAbortModal,
          //   theme: 'secondary',
          // },
          {
            text: this.$t('feedback.finishRecord'),
            action: this.stopRecording,
          },
        ];
      }
      return [
        {
          text: this.$t('feedback.start'),
          action: this.startRecording,
        },
      ];
    },
    error() {
      return {
        icon: {
          name: 'important',
          theme: 'error',
          view: 'circle',
          size: 'l',
        },
        title: {
          text: this.$t('feedback.badHardware'),
          size: 'l',
        },
        description: {
          text: this.$t('feedback.notPossible'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('feedback.goHome'),
            action: this.goToHomeScreen,
          },
        ],
      };
    },
    watchOverlay() {
      return {
        // title: {
        //   text: this.$t('feedback.videoTitle'),
        //   size: 'l',
        //   isCenter: true,
        // },
        actions: [
          {
            text: this.$t('feedback.rewrite'),
            action: this.startNewRecording,
          },
          {
            text: this.$t('feedback.view'),
            action: this.playVideo,
          },
          {
            text: this.$t('feedback.continue'),
            action: this.goToVideoSendScreen,
          },
        ],
      };
    },
    abortModal() {
      return {
        title: {
          text: this.$t('feedback.abortQuestion'),
          size: 'l',
          isCenter: true,
        },
        agreeText: this.$t('feedback.agree'),
        disagreeText: this.$t('feedback.disagree'),
      };
    },
    isVideoReady() {
      return Boolean(this.video);
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isRecording && !this.shouldAbort) {
      this.showAbortModal();
    } else {
      next();
    }
  },
  created() {
    this.clearVideo();
    this.startCamera();
    this.showPending(0);
    this.initActions([
      ['ACTION_REMAKE_VIDEO', this.startNewRecording],
      ['ACTION_PLAY_VIDEO', this.playVideo],
      ['ACTION_SEND_VIDEO', this.goToVideoSendScreen],
    ]);
  },
  methods: {
    ...mapMutations('feedback', [
      'setVideo',
      'clearVideo',
    ]),
    startNewRecording() {
      if (this.isRecording || !this.isCameraStarted) return;
      this.clearVideo();
      this.startRecording(); // функция из миксина
    },
    startRecording() {
      this.startVideoRecording(MAX_VIDEO_DURATION_IN_SECONDS); // функция из миксина
    },
    stopRecording() {
      this.stopVideoRecording(); // функция из миксина
      this.showVideoProcessing();
    },
    playVideo() {
      this.playVideoRecording(this.video); // функция из миксина
    },
    onCameraStart() {
      this.isCameraStarted = true;
      this.startNewRecording();
    },
    onVideoRecordStart() {
      // Этот колбек будет вызван из миксина, когда начнется запись
      this.isRecording = true;
      this.startTimer();
      this.hidePending();
    },
    onVideoRecordStop() {
      // Этот колбек будет вызван из миксина, когда запись остановлена
      this.isRecording = false;
      this.stopTimer();
    },
    onVideoReady(video) {
      // Этот колбек будет вызван из миксина, когда видео будет готово
      this.setVideo(video);
      this.hideVideoProcessing();
    },
    onTimerUpdate(time) {
      // Этот колбек будет вызван из миксина
      if (this.time !== time) {
        this.time = time;
      }
    },
    onCameraError() {
      // Этот колбек будет вызван из миксина, если прилетит ошибка камеры
      this.showError();
    },
    abortRecording() {
      this.setAbortFlag();
      this.goToBackScreen();
    },
    setAbortFlag() {
      this.shouldAbort = true;
    },
    beforeInteractionStopped() {
      this.setAbortFlag();
    },
    showVideoProcessing() {
      this.isVideoProcessing = true;
      this.showPending();
    },
    hideVideoProcessing() {
      this.isVideoProcessing = false;
      this.hidePending();
    },
    showAbortModal() {
      this.isAbortModalVisible = true;
    },
    hideAbortModal() {
      this.isAbortModalVisible = false;
    },
    goToVideoSendScreen() {
      if (!this.isVideoReady) return;
      this.$router.push(this.$router.options.paths.videoSend);
    },
  },
};
</script>
